import * as React from 'react';
import { Money as MoneyType } from '../../../../../types';
import cn from 'classnames';
import { useTheme } from '../../../../../theme';
import { useTranslation } from 'react-i18next';
import Tooltip from '../../../../../BaseComponents/Tooltip';
import SeatPrice from '../../../SeatPrice/SeatPrice';
import PassengerFilters from '../PassengerFilters/PassengerFilters';
import { SeatMapRestrictions } from '@websky/graphql';
import { useConfig } from '../../../../../context';
import { AllowedSeatMapFilters } from '../../../../types';
import { CdnIcon } from '@shared/ui';
import { buildDataAttributes } from '@shared/lib/dom';

export interface Props {
	className?: string;
	activeClassName?: string;
	passengerName: string;
	passengerAvatarText: string;
	passengerId: string;
	seatNumbers?: string[];
	price?: MoneyType;
	isFreeSeat?: boolean;
	onClick?: () => void;
	isActive?: boolean;
	isDisabled?: boolean;
	onClearSeat: () => void;
	recommend?: boolean;
	changeable?: boolean;
	showSelect?: boolean;
	tooltipTitle?: string;
	allowedFilters?: AllowedSeatMapFilters[];
	activeFilters?: SeatMapRestrictions[];
	filteredSeatsCount?: number;
}

const DesktopPassenger: React.FC<Props> = ({
	className,
	activeClassName,
	passengerName,
	passengerId,
	passengerAvatarText,
	seatNumbers,
	price,
	onClick,
	isActive,
	isDisabled,
	onClearSeat,
	recommend,
	changeable,
	isFreeSeat,
	showSelect = true,
	tooltipTitle,
	allowedFilters,
	activeFilters,
	filteredSeatsCount
}) => {
	const showFilters = useConfig().global.seatMap.showFilters;
	const isSelected = !!seatNumbers?.length;

	const theme = useTheme('SeatMap').Passenger;
	const { t } = useTranslation('SeatMap');

	const passenger = (
		<div
			className={cn(className, theme.item, {
				[theme.item_active]: isActive,
				[theme.item_recommend]: recommend,
				[theme.item_disabled]: isDisabled,
				[theme.item_withSeat]: isSelected,
				[activeClassName]: activeClassName && isActive
			})}
			{...buildDataAttributes({
				'test-id': 'passenger',
				'passenger-id': passengerId,
				'passenger-active': !!isActive
			})}
			onClick={onClick}
		>
			<div className={theme.main}>
				<div className={theme.avatar}>{passengerAvatarText}</div>

				<div className={theme.passengerName}>{passengerName}</div>

				{!isDisabled && (
					<>
						{isSelected ? (
							<div className={theme.payload}>
								<div className={theme.numbers}>{seatNumbers.join(', ')}</div>

								{isFreeSeat ? (
									<span className={theme.freeSeat__text}>{t('Free seat')}</span>
								) : (
									price && (
										<SeatPrice
											className={theme.seatPrice}
											moneyClassName={theme.money}
											money={price}
										/>
									)
								)}

								{recommend ? (
									<CdnIcon icon="Arrow/Chevron_right" />
								) : (
									changeable && (
										<div
											className={theme.clear}
											data-test-id="passenger-clear"
											onClick={onClearSeat}
										>
											<CdnIcon icon="Action/Close-Clear" />
										</div>
									)
								)}
							</div>
						) : (
							showSelect && <div className={cn(theme.payload, theme.select)}>{t('Select')}</div>
						)}
					</>
				)}
			</div>

			{!isDisabled && showFilters && !!allowedFilters?.length && (!isSelected || changeable) && (
				<PassengerFilters
					passengerId={passengerId}
					isActive={isActive}
					allowedFilters={allowedFilters}
					filteredSeatsCount={filteredSeatsCount}
					activeFilters={activeFilters}
					passengerName={passengerName}
				/>
			)}
		</div>
	);

	if (tooltipTitle) {
		return <Tooltip title={tooltipTitle}>{passenger}</Tooltip>;
	}

	return passenger;
};

export default DesktopPassenger;
